/**
 * Local cache of the last-known-good authenticated session.
 *
 * The app authenticates via an httpOnly cookie, so the client never has
 * direct access to a session token to persist. Instead, we cache the
 * (non-sensitive) user profile that came back from the last successful
 * auth response. On a fresh app launch with no network, this lets us
 * treat the user as still logged in — trusting the cache until the
 * server can actually confirm or reject it — instead of forcing them
 * back to the login screen just because we can't reach the server yet.
 *
 * This is intentionally NOT a source of truth for authorization: any
 * server response that actually rejects the session (401/403) still
 * clears this cache and logs the user out for real.
 */

export const LOGOUT_STORAGE_KEY = "encoaudit_explicit_logout"
let logoutInMemory = null
let sessionGeneration = 0

const STORAGE_KEY = "encoaudit_auth_session"

// A cached session older than this is treated as stale and ignored, so a
// device that hasn't been online in a long time doesn't stay "logged in"
// indefinitely on cached data alone.
const MAX_CACHE_AGE_MS = 30 * 24 * 60 * 60 * 1000 // 30 days

function getStorage() {
  try {
    return typeof window !== "undefined" ? window.localStorage : null
  } catch {
    return null
  }
}

export function saveCachedSession(user) {
  const storage = getStorage()
  if (!storage || !user || hasExplicitLogout()) return
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify({ user, cachedAt: Date.now() }))
  } catch (error) {
    console.log("[v0] Failed to cache session:", error?.message)
  }
}

export function loadCachedSession() {
  if (hasExplicitLogout()) return null
  const storage = getStorage()
  if (!storage) return null
  try {
    const raw = storage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed?.user || !parsed?.cachedAt) return null
    if (Date.now() - parsed.cachedAt > MAX_CACHE_AGE_MS) {
      storage.removeItem(STORAGE_KEY)
      return null
    }
    return parsed.user
  } catch (error) {
    console.log("[v0] Failed to read cached session:", error?.message)
    return null
  }
}

export function clearCachedSession() {
  const storage = getStorage()
  if (!storage) return
  try {
    storage.removeItem(STORAGE_KEY)
  } catch (error) {
    console.log("[v0] Failed to clear cached session:", error?.message)
  }
}


export function getLogoutState() {
  try {
    const saved = getStorage()?.getItem(LOGOUT_STORAGE_KEY)
    return saved ? JSON.parse(saved) : logoutInMemory
  } catch { return logoutInMemory }
}
export const hasExplicitLogout = () => Boolean(getLogoutState())
export const authSessionGeneration = () => sessionGeneration

export function markExplicitLogout() {
  sessionGeneration += 1
  logoutInMemory = { id: `${Date.now()}-${Math.random()}`, pending: true }
  try {
    getStorage()?.setItem(LOGOUT_STORAGE_KEY, JSON.stringify(logoutInMemory))
    getStorage()?.removeItem("token")
  } catch { /* The in-memory marker still prevents session restoration. */ }
  clearCachedSession()
}
export function markServerLogoutComplete(id) {
  const state = getLogoutState()
  if (!state || state.id !== id) return
  logoutInMemory = { ...state, pending: false }
  try { getStorage()?.setItem(LOGOUT_STORAGE_KEY, JSON.stringify(logoutInMemory)) } catch {}
}
// Only an explicit successful sign-in/registration clears the logout choice.
export function clearExplicitLogout() {
  sessionGeneration += 1
  logoutInMemory = null
  try { getStorage()?.removeItem(LOGOUT_STORAGE_KEY) } catch {}
}
